/**
 * Warren-themed Animation Variants
 *
 * Reusable animation variants for common UI patterns.
 * Use these with Framer Motion's `variants` prop for clean, declarative animations.
 */

import type { Variants } from 'framer-motion';
import { warrenSpring, gentleSpring, smoothTween, staggerChildren } from './presets';

/**
 * Container variant for staggered children animations
 * Use on parent elements to create cascading entrance animations
 *
 * @example
 * <motion.div variants={containerVariants} initial="hidden" animate="visible">
 *   <motion.div variants={itemVariants}>Item 1</motion.div>
 *   <motion.div variants={itemVariants}>Item 2</motion.div>
 * </motion.div>
 */
export const containerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      ...staggerChildren,
      when: 'beforeChildren',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      when: 'afterChildren',
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

/**
 * Item variant for use with containerVariants
 * Creates a slide-up + fade-in effect
 */
export const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: warrenSpring,
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: smoothTween,
  },
};

/**
 * Card variants with hover and tap states
 * Perfect for interactive cards in grids or lists
 */
export const cardVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: warrenSpring,
  },
  hover: {
    y: -8,
    transition: warrenSpring,
  },
  tap: {
    scale: 0.99,
    transition: warrenSpring,
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: smoothTween,
  },
};

/**
 * Button variants with interactive states
 * Includes hover, tap, and disabled states
 */
export const buttonVariants: Variants = {
  initial: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
    y: -2,
    transition: warrenSpring,
  },
  tap: {
    scale: 0.98,
    y: 0,
    transition: warrenSpring,
  },
  disabled: {
    opacity: 0.6,
    scale: 1,
  },
};

/**
 * Modal/Dialog variants
 * Scale + fade entrance from center
 */
export const modalVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: smoothTween,
  },
};

/**
 * Backdrop variants for modals, drawers
 * Simple fade in/out for overlay backgrounds
 */
export const backdropVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: smoothTween,
  },
  exit: {
    opacity: 0,
    transition: smoothTween,
  },
};

/**
 * Drawer/Sidebar variants
 * Slide in from the side
 */
export const drawerVariants: Variants = {
  hidden: {
    x: '-100%',
  },
  visible: {
    x: 0,
    transition: gentleSpring,
  },
  exit: {
    x: '-100%',
    transition: smoothTween,
  },
};

/**
 * Alert/Toast notification variants
 * Slide in from top with bounce
 */
export const alertVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -50,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: warrenSpring,
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: smoothTween,
  },
};

/**
 * Accordion/Collapsible content variants
 * Smooth height expansion
 */
export const accordionVariants: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: smoothTween,
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: gentleSpring,
  },
};

/**
 * Icon variants with playful rotation
 * Great for interactive icons, close buttons, etc.
 */
export const iconVariants: Variants = {
  initial: {
    scale: 1,
    rotate: 0,
  },
  hover: {
    scale: 1.15,
    rotate: 5,
    transition: warrenSpring,
  },
  tap: {
    scale: 0.95,
    rotate: -5,
    transition: warrenSpring,
  },
};

/**
 * Chip/Tag variants
 * Subtle scale on hover
 */
export const chipVariants: Variants = {
  initial: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
    transition: warrenSpring,
  },
  tap: {
    scale: 0.98,
    transition: warrenSpring,
  },
};

/**
 * List item variants with slide-in effect
 * Perfect for table rows, menu items
 */
export const listItemVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: warrenSpring,
  },
  hover: {
    x: 6,
    transition: warrenSpring,
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: smoothTween,
  },
};

/**
 * Page transition variants
 * Fade + slide for smooth page changes
 */
export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    x: -20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: gentleSpring,
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: smoothTween,
  },
};

/**
 * Form input variants
 * Subtle scale on focus
 */
export const inputVariants: Variants = {
  initial: {
    scale: 1,
  },
  focus: {
    scale: 1.02,
    transition: warrenSpring,
  },
  error: {
    x: [0, -10, 10, -10, 10, 0],
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  },
};

/**
 * Loading skeleton variants
 * Gentle pulse animation
 */
export const skeletonVariants: Variants = {
  initial: {
    opacity: 0.6,
  },
  pulse: {
    opacity: [0.6, 0.8, 0.6],
    transition: {
      duration: 1.5,
      ease: 'easeInOut',
      repeat: Infinity,
    },
  },
};

/**
 * Success checkmark animation
 * Scale in with bounce
 */
export const checkmarkVariants: Variants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: [0, 1.2, 1],
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeInOut',
    },
  },
};

/**
 * Floating action button (FAB) variants
 * Entrance with scale + rotation
 */
export const fabVariants: Variants = {
  hidden: {
    scale: 0,
    rotate: -90,
  },
  visible: {
    scale: 1,
    rotate: 0,
    transition: warrenSpring,
  },
  hover: {
    scale: 1.1,
    transition: warrenSpring,
  },
  tap: {
    scale: 0.95,
    transition: warrenSpring,
  },
  exit: {
    scale: 0,
    rotate: 90,
    transition: smoothTween,
  },
};

/**
 * Badge/notification dot variants
 * Pulse to draw attention
 */
export const badgeVariants: Variants = {
  initial: {
    scale: 1,
  },
  pulse: {
    scale: [1, 1.2, 1],
    transition: {
      duration: 2,
      ease: 'easeInOut',
      repeat: Infinity,
    },
  },
};
